export type LeadLocation = {
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  stateAbbr?: string;
  postalCode?: string;
  country?: string;
  ipInferredPostalCode?: string;
};

export enum LevelOfEducation {
  HIGH_SCHOOL = 'highschool',
  GED = 'ged',
  ASSOCIATES = 'associates',
  BACHELORS = 'bachelors',
  MASTERS = 'masters',
  DOCTORATE = 'doctorate',
}

export enum AcademicAwards {
  BSN = 'bsn',
  BSW = 'bsw',
  MBA = 'mba',
  MSW = 'msw',
  LAW_DEGREE = 'law-degree',
  TEACHING_CERTIFICATE = 'teaching-certificate',
  SPEECH_PATHOLOGY = 'bachelors-speech-pathology',
  BEHAVIORAL_SCIENCE = 'bachelors-behavioral-science',
}

export enum LicenseTypes {
  NURSING = 'nursing',
}

export enum WorkExperienceFieldOptions {
  K12TEACHER = 'k-12-teacher',
  K12ADMIN = 'k-12-admin',
  HIGHEREDUCATION = 'higher-education',
  PRIVATESECTOR = 'private-sector',
  GOVERNMENTNONPROFIT = 'government-non-profit',
  OTHER = 'other',
}

export enum UndergraduateMajorOptions {
  ARCHITECTURE = 'Architecture',
  BEHAVIORAL_SCIENCE = 'Behavioral Science',
  BIOLOGY = 'Biology',
  CHEMISTRY = 'Chemistry',
  CHEMICALENGINEERING = 'Chemical Engineering',
  ENGINEERING_AND_ENGINEERING_TECHNOLOGY = 'Engineering and Engineering Technology',
  HUMANITIES_SOCIAL_SCIENCES_EDUCATION = 'Humanities, Social Sciences and Education',
  MANAGEMENT = 'Management',
  INTERIORDESIGN = 'Interior Design',
  STUDIOARTS = 'Studio Arts',
  SCIENCE_AND_TECHNOLOGY_MANAGEMENT = 'Science and Technology Management',
  DIGITAL_MEDIA_DESIGN = 'Digital Media Design',
  BUSINESS = 'Business',
  COMPUTERENGINEERING = 'Computer Engineering',
  COMPUTERSCIENCE = 'Computer Science',
  ECONOMICS = 'Economics',
  EDUCATION = 'Education',
  ELECTRICALENGINEERING = 'Electrical Engineering',
  ENGINEERING = 'Engineering',
  ENGLISH = 'English',
  GOVERNMENT = 'Government',
  HISTORY = 'History',
  INFORMATIONTECHNOLOGY = 'Information Technology',
  MATHEMATICS = 'Mathematics',
  MECHANICALENGINEERING = 'Mechanical Engineering',
  OTHER = 'Other',
  OTHERENGINEERING = 'Other Engineering',
  OTHERSCIENCES = 'Other Sciences',
  NONSCIENCE = 'Non-Science',
  PHYSICALSCIENCE = 'Physical Science',
  PHYSICS = 'Physics',
  STATISTICS = 'Statistics',
  TEACHING = 'Teaching',
  WRITING = 'Writing',
}

export enum LearningPaceOptions {
  STRUCTURED = 'structured',
  SELF_PACED = 'self-paced',
}

export enum CurrentPermanentResidenceCountry {
  US = 'us',
  OTHER = 'other',
}

export enum CurrentCitizenship {
  US = 'us',
}

export enum ProgramTrack {
  FAMILYNURSING = 'family-nursing',
  NURSEMIDWIFERY = 'nurse-midwifery',
  WOMENSHEALTH = 'womens-health',
  AGACNP = 'adult-gerontology-acute-care-nurse-practitioner',
  ABA = 'applied-behavior-analysis',
  MAP = 'psychology',
  LMFT = 'licensed-marriage-and-family-therapist',
  LPC = 'licensed-professional-counselor',
  MAC = 'master-of-arts-in-counseling',
  MFT = 'master-of-science-in-marriage-and-family-therapy',
  MSE = 'mse-in-educational-leadership',
  MSEPL = 'mse-in-educational-leadership-with-principle-licensure',
  PLP = 'preparation-principal-licensure-preparation-only',
  MAT = 'master-of-arts-in-teaching',
  TESOL = 'master-of-arts-in-teaching-tesol',
  MSN = 'MSN',
  PostMSNtoDNP = 'Post-MSN to DNP',
  PostBSNFNP = 'Post-BSN - FNP',
  PostBSNAGACNP = 'Post-BSN - AG-ACNP',
  PostBSNMWH = 'Post-BSN - MWH',
  PostBSNWHNP = 'Post-BSN - WHNP',
  PostMasters = "Post-Master's",
}

type StartDate = string;

export default interface Lead {
  firstName: string;
  lastName: string;
  email: string;
  startDate?: StartDate | null;
  ipAddress?: string;
  phoneNumber?: string;
  location?: LeadLocation;
  hsGradYear?: number;
  levelOfEducation?: LevelOfEducation;
  inProgressLevelOfEducation?: LevelOfEducation;
  militaryStatus?: boolean;
  gpa?: number;
  tcpaText?: string;
  transferCredits?: number;
  yearsOfWorkExperience?: number;
  yearsOfProgramRelatedExperience?: number;
  currentAcademicAward?: AcademicAwards | null;
  inProgressAcademicAward?: AcademicAwards | null;
  currentLicense?: LicenseTypes | null;
  inProgressLicense?: LicenseTypes;
  currentCitizenship?: CurrentCitizenship | null;
  workExperienceField?: WorkExperienceFieldOptions | null;
  hasGivenTCPAConsent?: boolean;
  undergraduateMajor?: UndergraduateMajorOptions;
  inProgressUndergraduateMajor?: UndergraduateMajorOptions;
  learningPace?: LearningPaceOptions | null;
  currentPermanentResidenceCountry?: CurrentPermanentResidenceCountry | null;
  programTrack?: ProgramTrack | null;
  hasGivenThirdPartySharingConsent?: boolean;
  hasGivenProspectSharingConsent?: boolean;
  isInARelatedRole?: boolean;
  leadSource?:
    | string
    | 'spotlight'
    | 'recommender-popup'
    | 'recommender-embedded'
    | 'exact-matches'
    | 'related-matches'
    | null;
  tcpaEABConsentGiven?: boolean;
  tcpaEMCCConsentGiven?: boolean;
  someCollege?: boolean;
  is18YearsOrOlder?: boolean;
  lastAttendanceYear?: number;
  thirdPartyLeadId?: string;
  hasCompleted30SemesterHours?: boolean;
}

export type TrackingContext = {
  correlationId?: string;
  publisher?: string;
  trafficSource?: string;
  heclid?: string;
  anonymousId?: string;
  url?: string;
  experience?: string;
  publisherType?: string;
};

export type SchoolContext = {
  id: number;
  name?: string;
  slug?: string;
  campus?: {
    id?: number;
    name?: string;
    isOnline?: boolean;
  };
};

export type LeadProgramContext = {
  providerProgramId?: number;
  program?: string;
  degree?: {
    name?: string;
    slug?: string;
    id?: number;
  };
  category?: {
    name?: string;
    slug?: string;
    id?: number;
  };
  subject?: {
    name?: string;
    slug?: string;
    id?: number;
  };
  cap?: {
    id?: number;
  };
};

export type ContactContext = {
  contactId?: string;
};

export type MessageContext = {
  timestamp: number;
  guid: string;
  isSync: boolean;
};

export type LeadRequest = {
  lead: Lead;
  trackingContext: TrackingContext;
  schoolContext: SchoolContext;
  programContext: LeadProgramContext;
  contactContext?: ContactContext;
  messageContext?: MessageContext;
};

export type PropertySchema = {
  required?: string[];
  properties?: Record<string, unknown>;
  additionalProperties?: boolean;
};

export type SchoolSchema = {
  required: Array<keyof Lead>;
  properties: Record<keyof Lead, PropertySchema>;
  additionalProperties: boolean;
};

export type DiffyResponse = {
  diffy: SchoolSchema;
};
